import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@learn-me/ui";
import {
  Sparkles,
  Loader2,
  ChevronDown,
  ChevronUp,
  Layers,
} from "lucide-react";
import { adminApi } from "../api/adminApi";
import toast from "react-hot-toast";

interface ChapterAIToolsProps {
  chapterId: string;
  courseId: string;
  hasSections: boolean;
  onSectionsGenerated: () => void;
}

export function ChapterAITools({
  chapterId,
  courseId,
  onSectionsGenerated,
}: ChapterAIToolsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [useReferenceDocs, setUseReferenceDocs] = useState(true);

  // Check if course has reference docs
  const { data: referenceDocs = [] } = useQuery({
    queryKey: ["course-reference-docs", courseId],
    queryFn: () => adminApi.getCourseReferenceDocs(courseId),
    staleTime: 30000,
  });

  const hasReferenceDocs = referenceDocs.length > 0;
  const processedDocsCount = referenceDocs.filter((d) => d.isProcessed).length;

  const generateSectionsMutation = useMutation({
    mutationFn: () =>
      adminApi.generateChapterSections(chapterId, useReferenceDocs),
    onSuccess: (data) => {
      onSectionsGenerated();
      toast.success(`${data.sections.length} secciones generadas`);
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Error al generar secciones",
      );
    },
  });

  const isGenerating = generateSectionsMutation.isPending;

  return (
    <div className="bg-violet-50 border border-violet-200 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full px-3 py-2 bg-violet-100 hover:bg-violet-150 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-violet-600" />
          <span className="text-sm font-medium text-violet-800">
            Herramientas IA
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-violet-600" />
        ) : (
          <ChevronDown className="h-4 w-4 text-violet-600" />
        )}
      </button>

      {isExpanded && (
        <div className="p-3 space-y-3">
          {/* Reference docs toggle */}
          {hasReferenceDocs && (
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={useReferenceDocs}
                onChange={(e) => setUseReferenceDocs(e.target.checked)}
                className="w-3.5 h-3.5 text-violet-600 rounded focus:ring-violet-500"
                disabled={isGenerating}
              />
              <span className="text-violet-700">
                Usar docs de referencia ({processedDocsCount}/
                {referenceDocs.length})
              </span>
            </label>
          )}

          {/* Generate Sections */}
          <Button
            type="button"
            size="sm"
            className="w-full justify-start bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => generateSectionsMutation.mutate()}
            disabled={isGenerating}
          >
            {generateSectionsMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Layers className="h-4 w-4 mr-2" />
            )}
            Generar Secciones
          </Button>

          <p className="text-xs text-violet-600">
            Usa los botones de cada sección para generar diagramas
          </p>
        </div>
      )}
    </div>
  );
}
